﻿import { IInputScope, KbInput } from "@app/components/kb-input/kb-input.component";
import { NgComponent } from "@app/di/decorators";
import { Token } from "@app/di/token";
import { Dirs } from "@app/helpers/dirs";
import { Utils } from "@tools";

export interface IAutoCompleteScope extends IInputScope {
    /**
     * the query for data....(query:string) => ng.IPromise<any>
     */
    source: any;
    /**
     * custom data that can be passed in and accessed by the transcluded content for the select items
     */
    data: any;
    /**
     * query to get the label of a value.  Needed when the field you're querying is not
     * the same as the value field:  (value: string) => ng.IPromise<string>
     */
    getLabel: any;
    /**
     * whether it should constrain values to those from the source
     */
    editable: boolean;
    /**
     * needed for comparison to hide already selected items from the dropdown
     */
    valueField: string;
    labelField: string;
    itemStyle: string;
    /**
     * whether the number of remote queries is rate-limited;  defaults to false
     */
    throttle: string;
    /**
     * the minimum number of characters typed by the user before autocompletion kicks in.  Defaults to 0;
     */
    minCharacters: number;
    /**
     * an optional icon to show inside the auto-complete (used for search boxes)
     */
    icon: string;
    /**
     * happens when the user explicitly presses enter or selects an option from the dropdown 
     * (won't fire when just losing focus).  This is used by the search.
     */
    submit: (q: { item: any }) => void; //
    placeholder: string;
    mobileDropdown: boolean;

    // internal directive members
    _matches: any[];
    _activeIndex: number;
    _select: (item: any) => void;
    _focus: (e: ng.IAngularEvent) => void;
    _blur: (e: ng.IAngularEvent) => void;
    _dropdownOpen: boolean;
    _label: string;
    _inputChange: (e: ng.IAngularEvent) => void;
    _hint: string; // if the user is typing an exact match, will show the rest of the word (like google)
    _id: string;
    _isActive: (index) => boolean;
    _itemMousedown: (e: JQueryEventObject, item: any) => void;
    _inProgress: boolean; // tracking remote calls to show loading animation
}

export interface IKbAutoCompleteController {
    scope: IAutoCompleteScope;
}

@NgComponent({
    token: Token.KbAutoComplete,
    dependencies: [
        Token.$Q,
        Token.$Http,
        Token.$Timeout,
    ]
})
export class KbAutoComplete extends KbInput {

    constructor($q: ng.IQService, $http: ng.IHttpService, $timeout: ng.ITimeoutService) {
        super($timeout);

        this.template = Dirs.component("kb-auto-complete");
        this.scope = Utils.extend(this.scope, {
            source: "&",
            data: "=?",
            getLabel: "&",
            editable: "=?",
            valueField: "@",
            labelField: "@",
            defaultLabel: "@",
            headerTemplate: "@",
            itemStyle: "@",
            throttle: "@",
            minCharacters: "=?",
            icon: "@",
            submit: "&",
            placeholder: "@",
            mobileDropdown: "=?",
        });
        this.transclude = true;

        this.compile = (element, atts: any) => {
            if (atts.mobileDropdown == null) atts.mobileDropdown = "true";
            return this.link;
        };

        this.link = (scope: IAutoCompleteScope, element: JQuery, atts, controllers: any[]) => {
            this.kbInputLinkFn(scope, element, atts, controllers, true, (val => false));
            let ngModel = controllers[0];
            let input = element.find("input");
            scope._id = Utils.shortId();
            let hintItem: any; // the data item that is being used to show the current hint

            let toStringSafe = (o: any) => {
                if (o == null) return null;
                return o.toString();
            };

            let getItemValue = (item: any) => {
                if (item === null || item === undefined) return null;
                return scope.valueField ? item[scope.valueField] : item;
            };
            let getItemLabel = (item: any): string => {
                let l;
                if (item != null) {
                    if (scope.labelField && item[scope.labelField]) {
                        l = item[scope.labelField];
                    } else if (scope.valueField) {
                        l = item[scope.valueField];
                    } else {
                        l = item;
                    }
                }
                return toStringSafe(l);
            };

            let isEnabled = () => {
                return !scope.ngDisabled;
            };
                
            let setLabelForValue = (): ng.IPromise<any> => {
                // if the labelField is being used, then we need to make a 
                // call to the consumer to get the label for the value
                if (scope.labelField) {
                    // first we try to find if we already have a match in our _matches to get the label from there
                    // if not, then we make a call to the consumer to retrieve it
                    let match = scope._matches ? scope._matches.find(m => getItemValue(m) == ngModel.$viewValue) : null;
                    if (match) {
                        scope._label = getItemLabel(match);
                    } else {
                        return $q.when(scope.getLabel({ value: ngModel.$viewValue })).then(l => {
                            scope._label = l;
                        });
                    }
                } else { // no label-field is being used, so we just set the text input to the value
                    scope._label = ngModel.$viewValue;
                }
            };

            ngModel.$render = () => { // called when the binding has been changed by the consumer
                setLabelForValue();
            };

            scope._itemMousedown = (e, item) => {
                e.preventDefault(); // so we don't lose focus and trigger the blur event
                scope._select(item);
                // call submit for search boxes
                if (scope.submit) scope.submit({ item });
            };

            scope._select = item => {
                if (isEnabled()) {
                    hideDropdown();
                    ngModel.$setViewValue(getItemValue(item));
                    scope._label = getItemLabel(item);
                    scope._hint = null;
                    hintItem = null;
                    $timeout(() => {
                        suppressFocusEvent = true;
                        input.focus();
                        // suppressFocusEvent = false;
                    }, 0);
                }
            };
                                
            let showDropdown = () => {
                // scope._activeIndex = 0;
                scope._dropdownOpen = true;
            };
            let hideDropdown = () => {
                scope._dropdownOpen = false;
            };
                             
            scope._isActive = index => {
                return index === scope._activeIndex;
            };
                   
            let throttle = scope.throttle ? (scope.throttle == "true") : false;
            let getMatches = Utils.throttle(() => {
                if (isEnabled()) {
                    var query = toStringSafe(scope._label);
                    if (query && (!scope.minCharacters || query.length >= scope.minCharacters)){
                        scope._inProgress = true;
                        return $q.when(scope.source({ query })).then((result: any[]) => {
                            scope._matches = result;
                            if (scope._matches && scope._matches.length) showDropdown();
                            scope._hint = null;
                            hintItem = null;
                            if (scope._label) {
                                // if the first match is an exact match so far, then show the hint
                                if (scope._matches && scope._matches.length) {
                                    let o = scope._matches[0];
                                    let l = getItemLabel(o);
                                    if (l && l.toString().toLowerCase().startsWith(scope._label.toLowerCase())) {
                                        // match the case of whatever the user has already typed
                                        scope._hint = scope._label + l.substr(scope._label.length);
                                        hintItem = o;
                                    }
                                    scope._activeIndex = 0;
                                } else {
                                    scope._activeIndex = -1;
                                }
                                scope._activeIndex = scope.editable ? -1 : 0;
                            }
                        }).finally(() => {
                            scope._inProgress = false;
                        });
                    }
                }
            }, throttle ? 300 : 0);

            let suppressFocusEvent = false;
            scope._focus = e => {
                if (!suppressFocusEvent && !scope._dropdownOpen) {
                    getMatches();
                }
                suppressFocusEvent = false;
            };
            scope._blur = e => {
                // if this is editable, then set ngModel to whatever the user typed in when the input loses focus
                // also when a labelField is declared, it CAN'T be editable
                if (scope.editable) {
                    ngModel.$setViewValue(scope._label);
                } else {
                    setLabelForValue();
                }
                scope._hint = null;
                hintItem = null;
            };

            scope._inputChange = e => {
                getMatches();
            };

            let buildItem = val => {
                let item = null;
                if (scope._activeIndex > -1) {
                    item = scope._matches[scope._activeIndex];
                } else if (scope.valueField) {
                    item = {};
                    item[scope.valueField] = val;
                } else {
                    item = val;
                }
                return item;
            };

            let moveUp = () => {
                if (scope._dropdownOpen) {
                    // if we are already at index zero, then close the dropdown
                    let newIndex = scope._activeIndex - 1;
                    if (newIndex >= 0) scope._activeIndex = newIndex;
                }
            };

            let moveDown = () => {
                showDropdown();
                // if we are already at index zero, then close the dropdown
                let newIndex = scope._activeIndex + 1;
                if (newIndex < scope._matches.length) scope._activeIndex = newIndex;
            };
                
            // bind keyboard events: arrows up(38) / down(40), enter(13) and tab(9), esc(27)
            element.on("keydown", (e: JQueryEventObject) => {

                if (e.which == 38) {
                    e.preventDefault();
                    scope.$apply(() => {
                        moveUp();
                    });
                }
                if (e.which == 40) {
                    e.preventDefault();
                    scope.$apply(() => {
                        moveDown();
                    });
                }
                if (e.which == 9 || e.which == 13) {
                    scope.$apply(() => {
                        let item = buildItem(scope._label);
                        if ((scope._dropdownOpen && scope._activeIndex > -1) || scope.editable) {
                            scope._select(item);
                            e.stopPropagation();
                        }  else {
                            hideDropdown();
                            scope._blur(null);
                        }

                        // call submit for search boxes                            
                        if (scope.submit) {
                            scope.submit({ item });
                        }
                    });
                }
                if (e.which == 27) {
                    e.preventDefault();
                    scope.$apply(() => {
                        hideDropdown();
                        e.stopPropagation();
                    });
                }
            });

            scope.$on("$destroy", () => {
                element.off();
            });
        };
    }
}
